// material-ui
import { fade } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { AppLocalizations } from "constants/localization";
import {
  matchAnchorColor,
  matchCardBorderColor,
  matchGameTileTextColor,
  matchServeBallColor,
  matchSetTileColor,
  matchSetTileTextColor,
  matchTextColor,
  matchTileBorderColor,
} from "constants/pigments";
import { contextualStatus } from "constants/tennis";
import {
  gameTileLabels,
  getLastGame,
  getPlayerName,
  getTeams,
  setScoresForBoard,
} from "helpers";
import PlayerAvatar from "hooks/PlayerAvatar";
import React from "react";
import ScoreTile from "./ScoreTile";
import ServeBall from "./ServeBall";
// styles
import styles from "./styles";

const useStyles = makeStyles(styles);

// The match page's score card, ported from the app's MatchScoreGizmo: the two
// teams down the left with their avatars and flags, one column of tiles per
// set to the right, the game in play on white tiles, and the serving ball (or
// "Winner") in the gutter between them.
//
// Rows on both sides are exactly tileSize + 4 tall, which is what lines the
// player up with their score.
export default function MatchScoreCard({ tennisMatch }) {
  const classes = useStyles(styles);
  // The board's width is set by the number of sets, so on a phone everything
  // around it has to give: 44px tiles across five sets leave no room for a
  // name at all, and a doubles row has two of them plus two avatars.
  const compact = useMediaQuery("(max-width:600px)");
  const tileSize = compact ? 30 : 44;
  const rowHeight = tileSize + 4;
  const nameStyle = compact
    ? { fontSize: 12, paddingLeft: 6 }
    : { fontSize: 16, paddingLeft: 10 };
  const separatorStyle = compact ? { padding: "0 4px" } : undefined;

  const colorKey = tennisMatch.rule.color;
  const textColor = matchTextColor(colorKey);
  const borderColor = matchCardBorderColor(colorKey);
  const setTileColor = matchSetTileColor(colorKey);
  const setTileTextColor = matchSetTileTextColor(colorKey);
  const gameTileTextColor = matchGameTileTextColor(colorKey);

  const teams = getTeams(tennisMatch.players);
  const setScores = setScoresForBoard(tennisMatch);
  const lastSet = setScores[setScores.length - 1];
  const game = getLastGame(tennisMatch);
  const matchWon = (tennisMatch.winnerTeamID ?? 0) > 0;
  // As in the app: the game in play only gets a column while the last set is
  // still open.
  const gameInPlay = game && !(lastSet && lastSet.completed);
  const gameLabels = gameInPlay ? gameTileLabels(game) : ["", ""];
  const status = contextualStatus(
    tennisMatch.status && tennisMatch.status.contextualStatus
  );

  // A set tile is filled solid for the team that won the set and half-strength
  // for the other; an unfinished set is half-strength on both rows.
  const setTileFill = (score, team) =>
    score.completed && score.winnerTeamID === team
      ? setTileColor
      : fade(setTileColor, 0.5);

  const marker = (team) => {
    if (matchWon) {
      if (tennisMatch.winnerTeamID !== team) return null;
      return (
        <span className={classes.winnerLabel} style={{ color: textColor }}>
          {AppLocalizations.winner}
        </span>
      );
    }
    // servingTeamID rather than the app's serving PLAYER index: the card shows
    // one row per team, and in doubles the app's index can point at a player
    // who has no row of their own.
    if (!game || game.servingTeamID !== team) return null;
    return (
      <ServeBall
        size={Math.round(tileSize * 0.55)}
        color={matchServeBallColor(colorKey)}
      />
    );
  };

  return (
    <div className={classes.root}>
      <div
        className={classes.card}
        style={{
          borderColor: fade(borderColor, 0.45),
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.35), rgba(255,255,255,0.06))",
        }}
      >
        <div className={classes.players}>
          {teams.map((team, teamIndex) => (
            <div
              className={classes.playerRow}
              key={`team-${teamIndex}`}
              style={{ height: rowHeight }}
            >
              {team.map((player, playerIndex) => (
                <React.Fragment key={player.uuid || playerIndex}>
                  {playerIndex > 0 ? (
                    <span
                      className={classes.separator}
                      style={{ color: textColor, ...separatorStyle }}
                    >
                      /
                    </span>
                  ) : null}
                  <PlayerAvatar
                    player={player}
                    size={compact ? tileSize - 4 : tileSize - 6}
                    textColor={textColor}
                  />
                  <span
                    className={classes.name}
                    style={{ color: textColor, ...nameStyle }}
                  >
                    {getPlayerName(player)}
                  </span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>

        <div className={classes.board}>
          <div className={classes.markerColumn}>
            {[1, 2].map((team) => (
              <div
                className={classes.markerCell}
                key={`marker-${team}`}
                style={{ height: rowHeight }}
              >
                {marker(team)}
              </div>
            ))}
          </div>

          {setScores.map((score, setIndex) => (
            <div className={classes.column} key={`set-${setIndex}`}>
              {[1, 2].map((team) => {
                const fill = setTileFill(score, team);
                return (
                  <ScoreTile
                    key={team}
                    value={team === 1 ? score.scoreTeam1 : score.scoreTeam2}
                    exponent={
                      team === 1 ? score.exponentTeam1 : score.exponentTeam2
                    }
                    size={tileSize}
                    bgColor={fill}
                    borderColor={matchTileBorderColor(colorKey, fill)}
                    textColor={setTileTextColor}
                  />
                );
              })}
            </div>
          ))}

          {gameInPlay ? (
            <div className={classes.column}>
              {[1, 2].map((team) => (
                <ScoreTile
                  key={team}
                  value={gameLabels[team - 1]}
                  size={tileSize}
                  bgColor="#FFFFFF"
                  borderColor={matchTileBorderColor(
                    colorKey,
                    matchAnchorColor(colorKey)
                  )}
                  textColor={gameTileTextColor}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <div className={classes.status} style={{ color: textColor }}>
        {status}
      </div>
    </div>
  );
}
